import React, { useState, useMemo } from 'react';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  filterButton: {
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '9999px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    lineHeight: 1.4,
  },
  filterButtonActive: {
    background: '#1f2937',
    color: '#fff',
    borderColor: '#1f2937',
  },
  searchInput: {
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '6px 12px',
    outline: 'none',
    width: '220px',
    color: '#374151',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  pillGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#dcfce7',
    color: '#166534',
  },
  pillAmber: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fef3c7',
    color: '#92400e',
  },
  pillRed: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 600,
    background: '#fee2e2',
    color: '#991b1b',
  },
  tableWrapper: {
    maxHeight: '600px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  thRight: {
    textAlign: 'right',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  tdRight: {
    textAlign: 'right',
  },
  sortArrow: {
    marginLeft: '4px',
    fontSize: '10px',
    color: '#9ca3af',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

const STATUS_COLORS = {
  green: '#16a34a',
  amber: '#f59e0b',
  red: '#dc2626',
};

const STATUS_ORDER = { red: 0, amber: 1, green: 2 };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPrice(value) {
  if (value == null) return '--';
  if (typeof value === 'number') return `$${value.toFixed(2)}`;
  return '--';
}

function formatMosquito(value) {
  if (value === 'N/A' || value === 'not_available') return 'N/A';
  if (value == null) return '--';
  if (typeof value === 'number') return `$${value.toFixed(2)}`;
  return '--';
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '--';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function formatChangePercent(value) {
  if (value == null || value === 0) return '0.00%';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function getChangeColor(value) {
  if (value == null || value === 0) return '#6b7280';
  return value > 0 ? '#dc2626' : '#16a34a';
}

function getSortArrow(sortKey, currentSort) {
  if (currentSort.key !== sortKey) return '';
  return currentSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
}

const COLUMNS = [
  { key: 'address', label: 'Address', align: 'left' },
  { key: 'state', label: 'State', align: 'left' },
  { key: 'status', label: 'Status', align: 'left' },
  { key: 'pest365', label: 'Pest365', align: 'right' },
  { key: 'pest365Plus', label: 'Pest365+', align: 'right' },
  { key: 'oneTime', label: 'One-Time', align: 'right' },
  { key: 'initialServiceVisit', label: 'ISV', align: 'right' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick', align: 'right' },
  { key: 'changePercent', label: 'Change%', align: 'right' },
  { key: 'lastScrapeDate', label: 'Last Scrape', align: 'left' },
];

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'green', label: 'Green Only' },
  { key: 'amber', label: 'Amber Only' },
  { key: 'red', label: 'Red Only' },
];

export default function RAGStatusTable({ data }) {
  const [sortConfig, setSortConfig] = useState({ key: 'status', dir: 'asc' });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);

  const counts = useMemo(() => {
    if (!data) return { green: 0, amber: 0, red: 0 };
    return {
      green: data.filter((d) => d.status === 'green').length,
      amber: data.filter((d) => d.status === 'amber').length,
      red: data.filter((d) => d.status === 'red').length,
    };
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows = [...data];

    if (filter !== 'all') {
      rows = rows.filter((r) => r.status === filter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.address && r.address.toLowerCase().includes(q)) ||
          (r.city && r.city.toLowerCase().includes(q)) ||
          (r.state && r.state.toLowerCase().includes(q))
      );
    }

    return rows;
  }, [data, filter, search]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      let aVal, bVal;

      if (sortConfig.key === 'status') {
        aVal = STATUS_ORDER[a.status] != null ? STATUS_ORDER[a.status] : 3;
        bVal = STATUS_ORDER[b.status] != null ? STATUS_ORDER[b.status] : 3;
      } else if (sortConfig.key === 'mosquitoTickControl') {
        aVal = typeof a.mosquitoTickControl === 'number' ? a.mosquitoTickControl : -Infinity;
        bVal = typeof b.mosquitoTickControl === 'number' ? b.mosquitoTickControl : -Infinity;
      } else if (sortConfig.key === 'lastScrapeDate') {
        aVal = a.lastScrapeDate ? new Date(a.lastScrapeDate).getTime() : 0;
        bVal = b.lastScrapeDate ? new Date(b.lastScrapeDate).getTime() : 0;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;

      // Secondary sort by state then address
      if (sortConfig.key !== 'state') {
        const sa = (a.state || '').toLowerCase();
        const sb = (b.state || '').toLowerCase();
        if (sa < sb) return -1;
        if (sa > sb) return 1;
      }
      if (sortConfig.key !== 'address') {
        const aa = (a.address || '').toLowerCase();
        const ab = (b.address || '').toLowerCase();
        if (aa < ab) return -1;
        if (aa > ab) return 1;
      }
      return 0;
    });
    return sorted;
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Data Quality & Stability</div>
        <div style={styles.subtitle}>Data quality and price stability across all tracked addresses</div>
        <div style={styles.emptyState}>No address status data available</div>
      </div>
    );
  }

  function renderCellValue(col, row) {
    switch (col.key) {
      case 'address':
        return row.address || '--';
      case 'state':
        return row.state || '--';
      case 'status':
        return (
          <div style={styles.statusCell}>
            <span
              style={{
                ...styles.statusDot,
                backgroundColor: STATUS_COLORS[row.status] || '#6b7280',
              }}
            />
            <span>{row.statusReason || row.status}</span>
          </div>
        );
      case 'pest365':
        return formatPrice(row.pest365);
      case 'pest365Plus':
        return formatPrice(row.pest365Plus);
      case 'oneTime':
        return formatPrice(row.oneTime);
      case 'initialServiceVisit':
        return formatPrice(row.initialServiceVisit);
      case 'mosquitoTickControl': {
        const val = formatMosquito(row.mosquitoTickControl);
        if (val === 'N/A') {
          return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>;
        }
        return val;
      }
      case 'changePercent':
        return (
          <span style={{ color: getChangeColor(row.changePercent), fontWeight: 600 }}>
            {formatChangePercent(row.changePercent)}
          </span>
        );
      case 'lastScrapeDate':
        return formatDate(row.lastScrapeDate);
      default:
        return '--';
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Data Quality & Stability</div>
      <div style={styles.subtitle}>Data quality and price stability across all tracked addresses</div>

      <div style={styles.summaryBar}>
        <span style={styles.pillGreen}>{counts.green} Green</span>
        <span style={styles.pillAmber}>{counts.amber} Amber</span>
        <span style={styles.pillRed}>{counts.red} Red</span>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.filterGroup}>
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              style={{
                ...styles.filterButton,
                ...(filter === opt.key ? styles.filterButtonActive : {}),
              }}
              onClick={() => setFilter(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search address, city, or state..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...styles.th,
                    ...(col.align === 'right' ? styles.thRight : {}),
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <span style={styles.sortArrow}>{getSortArrow(col.key, sortConfig)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => {
              const isEven = idx % 2 === 0;
              const isHovered = hoveredRow === idx;
              const baseBg = isEven ? '#fff' : '#fafafa';
              const bg = isHovered ? '#f9fafb' : baseBg;

              return (
                <tr
                  key={row.addressId || idx}
                  style={{
                    backgroundColor: bg,
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        ...styles.td,
                        ...(col.align === 'right' ? styles.tdRight : {}),
                        ...(col.key === 'address'
                          ? { maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }
                          : {}),
                      }}
                    >
                      {renderCellValue(col, row)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: '24px' }}>
                  No addresses match the current filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
